import { PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { CustomerAuthService } from './customer-auth.service';

/**
 * Protects customer-only routes. SSR-safe: on the server (no localStorage) it allows through and
 * lets the browser enforce, so an authenticated user is never falsely redirected during SSR.
 * Expiry-aware: a stale token is silently refreshed; only a dead session (refresh failed too)
 * redirects to login — so an expired-but-refreshable session no longer renders broken pages.
 */
export const customerAuthGuard: CanActivateFn = async (_route, state) => {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) return true;

  const auth = inject(CustomerAuthService);
  // Both injections MUST happen before the first await: Angular's injection context does not
  // survive an await boundary, so injecting the Router after one throws NG0203 and the navigation
  // dies silently — leaving a signed-out visitor on a blank page instead of the login redirect.
  const router = inject(Router);

  if (await auth.ensureAuthenticated()) return true;
  auth.clearSession(); // drop the dead token so the UI reads signed-out everywhere

  return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
};
